/* eslint-disable */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiRequest } from "../../utils/api";

const requestJSON = async (path, options = {}) => {
  const res = await apiRequest(path, options);

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      msg = j?.detail || j?.error || JSON.stringify(j) || msg;
    } catch {}
    throw new Error(msg);
  }

  if (res.status === 204) return null;
  return res.json();
};

export const fetchJobs = createAsyncThunk("jobs/fetchJobs", async () => {
  const data = await requestJSON("/jobs/");
  return Array.isArray(data) ? data : data.results || [];
});

export const createJob = createAsyncThunk("jobs/createJob", async (payload) => {
  return requestJSON("/jobs/", {
    method: "POST",
    body: JSON.stringify(payload),
  });
});

export const updateJob = createAsyncThunk("jobs/updateJob", async ({ id, data }) => {
  return requestJSON(`/jobs/${id}/`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
});

export const deleteJob = createAsyncThunk("jobs/deleteJob", async (id) => {
  await requestJSON(`/jobs/${id}/`, { method: "DELETE" });
  return id;
});

export const fetchJobTypes = createAsyncThunk("jobs/fetchJobTypes", async () => {
  const data = await requestJSON("/job-types/");
  return Array.isArray(data) ? data : data.results || [];
});

export const createJobType = createAsyncThunk("jobs/createJobType", async (payload) => {
  return requestJSON("/job-types/", {
    method: "POST",
    body: JSON.stringify(payload),
  });
});

export const updateJobType = createAsyncThunk("jobs/updateJobType", async ({ id, data }) => {
  return requestJSON(`/job-types/${id}/`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
});

export const deleteJobType = createAsyncThunk("jobs/deleteJobType", async (id) => {
  await requestJSON(`/job-types/${id}/`, { method: "DELETE" });
  return id;
});

export const fetchShifts = createAsyncThunk("jobs/fetchShifts", async () => {
  const data = await requestJSON("/shifts/");
  return Array.isArray(data) ? data : data.results || [];
});

export const createShift = createAsyncThunk("jobs/createShift", async (payload) => {
  return requestJSON("/shifts/", {
    method: "POST",
    body: JSON.stringify(payload),
  });
});

export const updateShift = createAsyncThunk("jobs/updateShift", async ({ id, data }) => {
  return requestJSON(`/shifts/${id}/`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
});

export const deleteShift = createAsyncThunk("jobs/deleteShift", async (id) => {
  await requestJSON(`/shifts/${id}/`, { method: "DELETE" });
  return id;
});

export const fetchPaymentCriteria = createAsyncThunk("jobs/fetchPaymentCriteria", async () => {
  const data = await requestJSON("/payment-criteria/");
  return Array.isArray(data) ? data : data.results || [];
});

export const createPaymentCriteria = createAsyncThunk("jobs/createPaymentCriteria", async (payload) => {
  return requestJSON("/payment-criteria/", {
    method: "POST",
    body: JSON.stringify(payload),
  });
});

export const updatePaymentCriteria = createAsyncThunk("jobs/updatePaymentCriteria", async ({ id, data }) => {
  return requestJSON(`/payment-criteria/${id}/`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
});

export const deletePaymentCriteria = createAsyncThunk("jobs/deletePaymentCriteria", async (id) => {
  await requestJSON(`/payment-criteria/${id}/`, { method: "DELETE" });
  return id;
});

export const updateJobDay = createAsyncThunk("jobs/updateJobDay", async ({ id, data }) => {
  return requestJSON(`/job-days/${id}/`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
});

export const updateJobDayShift = createAsyncThunk("jobs/updateJobDayShift", async ({ id, data }) => {
  return requestJSON(`/job-day-shifts/${id}/`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
});

export const createJobAssignment = createAsyncThunk("jobs/createJobAssignment", async (payload) => {
  return requestJSON("/job-assignments/", {
    method: "POST",
    body: JSON.stringify(payload),
  });
});

export const updateJobAssignment = createAsyncThunk("jobs/updateJobAssignment", async ({ id, data }) => {
  return requestJSON(`/job-assignments/${id}/`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
});

export const deleteJobAssignment = createAsyncThunk("jobs/deleteJobAssignment", async (id) => {
  await requestJSON(`/job-assignments/${id}/`, { method: "DELETE" });
  return id;
});

const initialState = {
  jobs: [],
  jobTypes: [],
  shifts: [],
  paymentCriteria: [],
  loading: false,
  saving: false,
  error: null,
};

const replaceById = (list, item) => list.map((row) => (row.id === item.id ? item : row));

const jobsSlice = createSlice({
  name: "jobs",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.jobs = action.payload;
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || "Failed to load jobs";
      })
      .addCase(createJob.pending, (state) => {
        state.saving = true;
      })
      .addCase(createJob.fulfilled, (state, action) => {
        state.saving = false;
        state.jobs.unshift(action.payload);
      })
      .addCase(createJob.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error?.message || "Failed to create job";
      })
      .addCase(updateJob.fulfilled, (state, action) => {
        state.jobs = replaceById(state.jobs, action.payload);
      })
      .addCase(deleteJob.fulfilled, (state, action) => {
        state.jobs = state.jobs.filter((row) => row.id !== action.payload);
      })
      .addCase(updateJobDay.fulfilled, (state, action) => {
        const updatedDay = action.payload;
        state.jobs = state.jobs.map((job) =>
          job.id === updatedDay.job
            ? {
                ...job,
                job_days: (job.job_days || []).map((day) => (day.id === updatedDay.id ? updatedDay : day)),
              }
            : job
        );
      })
      .addCase(updateJobDayShift.fulfilled, (state, action) => {
        const updatedShift = action.payload;
        state.jobs = state.jobs.map((job) => ({
          ...job,
          job_days: (job.job_days || []).map((day) =>
            day.id === updatedShift.job_day
              ? {
                  ...day,
                  day_shifts: (day.day_shifts || []).map((shift) =>
                    shift.id === updatedShift.id ? updatedShift : shift
                  ),
                }
              : day
          ),
        }));
      })
      .addCase(createJobAssignment.fulfilled, (state, action) => {
        const assignment = action.payload;
        state.jobs = state.jobs.map((job) => {
          const jobId =
            assignment.job ||
            assignment.job_detail?.id ||
            assignment.job_day_detail?.job ||
            assignment.job_day_shift_detail?.job_day_detail?.job;

          if (job.id !== jobId) return job;

          return {
            ...job,
            job_days: (job.job_days || []).map((day) => {
              if (assignment.job_day && day.id === assignment.job_day) {
                return {
                  ...day,
                  assignments: [...(day.assignments || []), {
                    id: assignment.id,
                    crew_member_id: assignment.crew_member,
                    crew_name: `${assignment.crew_member_detail?.first_name || ""} ${assignment.crew_member_detail?.family_name || ""}`.trim(),
                    status: assignment.status,
                  }],
                };
              }
              return {
                ...day,
                day_shifts: (day.day_shifts || []).map((shift) =>
                  assignment.job_day_shift && shift.id === assignment.job_day_shift
                    ? {
                        ...shift,
                        assignments: [...(shift.assignments || []), {
                          id: assignment.id,
                          crew_member_id: assignment.crew_member,
                          crew_name: `${assignment.crew_member_detail?.first_name || ""} ${assignment.crew_member_detail?.family_name || ""}`.trim(),
                          status: assignment.status,
                        }],
                      }
                    : shift
                ),
              };
            }),
          };
        });
      })
      .addCase(fetchJobTypes.fulfilled, (state, action) => {
        state.jobTypes = action.payload;
      })
      .addCase(createJobType.fulfilled, (state, action) => {
        state.jobTypes.push(action.payload);
      })
      .addCase(updateJobType.fulfilled, (state, action) => {
        state.jobTypes = replaceById(state.jobTypes, action.payload);
      })
      .addCase(deleteJobType.fulfilled, (state, action) => {
        state.jobTypes = state.jobTypes.filter((row) => row.id !== action.payload);
      })
      .addCase(fetchShifts.fulfilled, (state, action) => {
        state.shifts = action.payload;
      })
      .addCase(createShift.fulfilled, (state, action) => {
        state.shifts.push(action.payload);
      })
      .addCase(updateShift.fulfilled, (state, action) => {
        state.shifts = replaceById(state.shifts, action.payload);
      })
      .addCase(deleteShift.fulfilled, (state, action) => {
        state.shifts = state.shifts.filter((row) => row.id !== action.payload);
      })
      .addCase(fetchPaymentCriteria.fulfilled, (state, action) => {
        state.paymentCriteria = action.payload;
      })
      .addCase(createPaymentCriteria.fulfilled, (state, action) => {
        state.paymentCriteria.push(action.payload);
      })
      .addCase(updatePaymentCriteria.fulfilled, (state, action) => {
        state.paymentCriteria = replaceById(state.paymentCriteria, action.payload);
      })
      .addCase(deletePaymentCriteria.fulfilled, (state, action) => {
        state.paymentCriteria = state.paymentCriteria.filter((row) => row.id !== action.payload);
      });
  },
});

export default jobsSlice.reducer;

export const selectJobs = (state) => state.jobs.jobs || [];
export const selectJobTypes = (state) => state.jobs.jobTypes || [];
export const selectShifts = (state) => state.jobs.shifts || [];
export const selectPaymentCriteria = (state) => state.jobs.paymentCriteria || [];
export const selectJobsLoading = (state) => state.jobs.loading;
